/**
 * tenant-bootstrap.ts — resolve tenant antes do app inicializar.
 *
 * Fluxo:
 *   1. Se hostname é DEV (localhost) ou OctaWash root domain →
 *      usa extrairSubdominioTenant (sync, sem network).
 *   2. Se hostname é CUSTOM DOMAIN do cliente →
 *      chama RPC fn_resolver_tenant_por_hostname via fetch raw
 *      (sem cliente supabase ainda — pra evitar circular boot).
 *   3. Resultado é cacheado em sessionStorage por TTL pra evitar
 *      latência em recargas subsequentes.
 *
 * Chame em main.tsx ANTES de criar o supabase client e renderizar app.
 */
import { extrairSubdominioTenant, isHostnameOctaWash } from './tenant';

const CACHE_KEY = 'octawash:tenant-bootstrap';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min

interface BootstrapCache {
  hostname: string;
  cod_tenant: string | null;
  expira_em: number;
}

interface RpcResult {
  seq_tenant: number;
  cod_tenant: string;
  nom_tenant: string;
  des_subdominio: string;
  des_dominio_proprio_admin: string | null;
  des_dominio_proprio_cliente: string | null;
  cod_portal: 'admin' | 'cliente' | 'subdominio';
}

function lerCache(hostname: string): string | null | undefined {
  try {
    if (typeof sessionStorage === 'undefined') return undefined;
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return undefined;
    const c: BootstrapCache = JSON.parse(raw);
    if (c.hostname !== hostname) return undefined;
    if (Date.now() > c.expira_em) return undefined;
    return c.cod_tenant;
  } catch {
    return undefined;
  }
}

function gravarCache(hostname: string, cod_tenant: string | null): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    const c: BootstrapCache = {
      hostname,
      cod_tenant,
      expira_em: Date.now() + CACHE_TTL_MS,
    };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(c));
  } catch {
    /* sessionStorage cheio ou indisponível — segue sem cache */
  }
}

/**
 * Resolve o cod_tenant via RPC chamada por fetch raw (sem cliente supabase).
 * Necessário porque o cliente supabase ainda não existe durante bootstrap.
 */
async function chamarRpcResolverHostname(
  url: string,
  anonKey: string,
  hostname: string,
): Promise<RpcResult | null> {
  try {
    const resposta = await fetch(`${url}/rest/v1/rpc/fn_resolver_tenant_por_hostname`, {
      method: 'POST',
      headers: {
        apikey: anonKey,
        Authorization: `Bearer ${anonKey}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ p_hostname: hostname }),
      // Timeout curto pra não travar boot
      signal: AbortSignal.timeout(3000),
    });
    if (!resposta.ok) return null;
    const dados = (await resposta.json()) as RpcResult[] | RpcResult;
    const primeiro = Array.isArray(dados) ? dados[0] : dados;
    return primeiro ?? null;
  } catch {
    return null;
  }
}

/**
 * Resolve o cod_tenant pro hostname atual.
 *
 * Estratégia:
 *   1. Cache sessionStorage hit → retorna imediato (sync).
 *   2. Hostname é OctaWash root domain → extrairSubdominioTenant (sync).
 *   3. Hostname é custom domain → RPC fn_resolver_tenant_por_hostname (async).
 *   4. Resultado é cacheado pra próxima carga.
 *
 * @returns cod_tenant ou null (se hostname não reconhecido)
 */
export async function bootstrapTenant(opcoes: {
  hostname: string;
  supabaseUrl: string;
  anonKey: string;
  fallbackTenant?: string | null;
}): Promise<string | null> {
  const { hostname, supabaseUrl, anonKey, fallbackTenant } = opcoes;

  // 1) Cache
  const cacheado = lerCache(hostname);
  if (cacheado !== undefined) {
    return cacheado ?? fallbackTenant ?? null;
  }

  // 2) Sync — é dominio OctaWash conhecido (sub.octasistemas.com etc)
  if (isHostnameOctaWash(hostname)) {
    const cod = extrairSubdominioTenant(hostname) ?? fallbackTenant ?? null;
    gravarCache(hostname, cod);
    return cod;
  }

  // 3) Async — custom domain do cliente
  const rpc = await chamarRpcResolverHostname(supabaseUrl, anonKey, hostname);
  const cod = rpc?.cod_tenant ?? fallbackTenant ?? null;
  gravarCache(hostname, cod);
  return cod;
}
